import React, { useState, useEffect, useMemo, useRef } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import "../../assets/css/MailRecipientsModal.css";

const GROUPS = [
  { key: "lead", title: "Lead" },
  { key: "coLead", title: "Co-Lead" },
  { key: "mentor", title: "Mentor" },
  { key: "rtp", title: "RTP" },
  {
    key: "steeringCommittee",
    title: "Steering Committee",
    addText: "Add member",
    addLink: "/admin-dashboard",
  },
  {
    key: "admins",
    title: "Retreat Administrators",
    addText: "Add administrator",
  },
];

const retreatLabel = (retreat) => {
  const name = [
    retreat?.parishName,
    retreat?.language
      ? `(${retreat.language[0].toUpperCase()}${retreat.language.slice(1)})`
      : null,
    retreat?.location
      ? `${retreat.location[0].toUpperCase()}${retreat.location.slice(1)}`
      : null,
  ]
    .filter(Boolean)
    .join(" ");
  return name || retreat?.title || "this retreat";
};

const personKey = (person) =>
  person.key ||
  (person.type === "couple" ? `couple:${person.id}` : `user:${person.id}`);

const personEmails = (person) => {
  const emails = Array.isArray(person.emails)
    ? person.emails
    : [person.hisEmail, person.herEmail, person.email];
  return [...new Set(emails.map((email) => String(email || "").trim()).filter(Boolean))];
};

const personName = (person) =>
  person.name ||
  `${person.firstName || ""} ${person.lastName || ""}`.trim() ||
  "Recipient";

const CLOSURE_EMAIL_SUBJECT =
  "Retreat Closure – Pending Data Validation and Completion";

function MailRecipientsModal({
  show,
  handleClose,
  retreatDetail,
  fetchRecipients,
  initialMessage = "",
  actionFunction,
  cancel = "Cancel",
  okay = "Send",
}) {
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState({});
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [message, setMessage] = useState(initialMessage);
  const [subject, setSubject] = useState(CLOSURE_EMAIL_SUBJECT);
  const [additionalEmails, setAdditionalEmails] = useState([]);
  const [customEmail, setCustomEmail] = useState("");
  const [autoRemind, setAutoRemind] = useState(false);
  const [retreatClosed, setRetreatClosed] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const fetchRecipientsRef = useRef(fetchRecipients);
  const initialMessageRef = useRef(initialMessage);
  fetchRecipientsRef.current = fetchRecipients;
  initialMessageRef.current = initialMessage;

  useEffect(() => {
    if (!show || !retreatDetail?.id) return undefined;

    let active = true;
    setLoading(true);
    setFetchError("");
    setMessage(initialMessageRef.current);
    setSubject(CLOSURE_EMAIL_SUBJECT);
    setSelectedKeys(new Set());
    setAdditionalEmails([]);
    setCustomEmail("");
    setAutoRemind(false);
    setGroups({});

    fetchRecipientsRef
      .current(retreatDetail.id)
      .then((data) => {
        if (!active) return;
        const nextGroups = {};
        GROUPS.forEach((group) => {
          nextGroups[group.key] = data?.[group.key] || [];
        });
        setGroups(nextGroups);
        setRetreatClosed(Boolean(data?.retreatClosed));

        const rosterUrl = data?.frontAddress
          ? `${String(data.frontAddress).replace(/\/+$/, "")}/retreatRoster?retreatId=${retreatDetail.id}`
          : "";
        const withRosterLink = (html) => {
          if (!rosterUrl) return html || "";
          return String(html || "")
            .replace(/#RosterLink#/g, rosterUrl)
            .replace(/https?:\/\/[^"'\s>]+\/retreatRoster\?retreatId=\d+/g, rosterUrl)
            .replace(/Open this retreat and complete the pending details/g, rosterUrl);
        };

        const everyone = Object.values(nextGroups).flat();
        const reminder = data?.reminder;
        if (reminder) {
          setSelectedKeys(
            new Set(
              (reminder.recipientKeys || []).filter(
                (key) => !String(key).startsWith("email:"),
              ),
            ),
          );
          setAdditionalEmails(reminder.additionalEmails || []);
          setAutoRemind(Boolean(reminder.enabled) && !data?.retreatClosed);
          setMessage(withRosterLink(initialMessageRef.current));
        } else {
          setMessage(withRosterLink(initialMessageRef.current));
          setSelectedKeys(
            new Set(
              everyone
                .filter((person) => personEmails(person).length > 0)
                .map(personKey),
            ),
          );
        }
      })
      .catch(() => {
        if (active) setFetchError("Failed to load recipients. Please try again.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [show, retreatDetail]);

  const allPeople = useMemo(
    () => Object.values(groups).flat(),
    [groups],
  );
  const selectableKeys = useMemo(() => {
    const keys = allPeople
      .filter((person) => personEmails(person).length > 0)
      .map(personKey);
    return [...new Set(keys)];
  }, [allPeople]);
  const allSelected =
    selectableKeys.length > 0 &&
    selectableKeys.every((key) => selectedKeys.has(key));

  const toggleKey = (key) =>
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const toggleMany = (keys, turnOn) =>
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      keys.forEach((key) => (turnOn ? next.add(key) : next.delete(key)));
      return next;
    });

  const handleAddCustomEmail = () => {
    const emailStr = customEmail.trim();
    if (!emailStr) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailStr)) {
      setFetchError("Please enter a valid email address.");
      return;
    }
    if (additionalEmails.some((email) => email.toLowerCase() === emailStr.toLowerCase())) {
      setFetchError("Email address already added.");
      return;
    }
    setAdditionalEmails((prev) => [...prev, emailStr]);
    setCustomEmail("");
    setFetchError("");
  };

  const handleSend = () => {
    setFetchError("");
    const selectedPeople = [];
    const seen = new Set();
    allPeople.forEach((person) => {
      const key = personKey(person);
      if (!selectedKeys.has(key) || seen.has(key)) return;
      seen.add(key);
      selectedPeople.push(person);
    });
    if (selectedPeople.length === 0 && additionalEmails.length === 0) {
      setFetchError("Select at least one person or add an email address.");
      return;
    }
    if (!subject.trim()) {
      setFetchError("Please enter a subject.");
      return;
    }
    const stripped = message
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, "")
      .trim();
    if (!stripped) {
      setFetchError("Please enter a message.");
      return;
    }

    actionFunction({
      subject: subject.trim(),
      message: message.replace(/<a\b([^>]*)>([\s\S]*?)<\/a>/gi, (full, attrs, inner) => {
        const href = /href\s*=\s*["']([^"']*)["']/i.exec(attrs);
        const url = href ? href[1] : "";
        if (/^mailto:/i.test(url) || /retreatRoster/i.test(url)) return full;
        return inner;
      }),
      recipients: selectedPeople.map((person) => ({
        type: person.type || (String(personKey(person)).startsWith("couple:") ? "couple" : "user"),
        id: person.id,
        email: person.email,
      })),
      additionalEmails,
      autoRemind: retreatClosed ? false : autoRemind,
    });
  };

  const handleModalClose = () => {
    setFetchError("");
    handleClose();
  };

  const selectedVisibleCount = selectableKeys.filter((key) =>
    selectedKeys.has(key),
  ).length;
  const selectedCount = selectedVisibleCount + additionalEmails.length;

  return (
    <Modal
      className="crm-modal"
      dialogClassName="crm-dialog"
      size="lg"
      show={show}
      onHide={handleModalClose}
      centered
    >
      <Modal.Header closeButton>
        <div>
          <p className="crm-kicker">Closure reminder</p>
          <Modal.Title>{retreatLabel(retreatDetail)}</Modal.Title>
        </div>
      </Modal.Header>

      <Modal.Body>
        <div className="crm-recipients">
          <div className="crm-recipients-head">
            <strong>
              Recipients
              {!loading && selectableKeys.length > 0
                ? ` · ${selectedVisibleCount} of ${selectableKeys.length}`
                : ""}
            </strong>
            {!loading && selectableKeys.length > 0 && (
              <button
                type="button"
                className="crm-text-btn"
                onClick={() => toggleMany(selectableKeys, !allSelected)}
              >
                {allSelected ? "Clear all" : "Select all"}
              </button>
            )}
          </div>

          {loading ? (
            <div className="crm-loading">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading recipients…
            </div>
          ) : (
            <div className="crm-grid">
              {GROUPS.map((group) => {
                const list = groups[group.key] || [];
                const keys = list
                  .filter((person) => personEmails(person).length > 0)
                  .map(personKey);
                const groupAll =
                  keys.length > 0 && keys.every((key) => selectedKeys.has(key));
                const addLink =
                  group.key === "admins" && retreatDetail?.id
                    ? `/create-retreat/${retreatDetail.id}`
                    : group.addLink;
                return (
                  <section className="crm-card" key={group.key}>
                    <div className="crm-section-head">
                      <h3>{group.title}</h3>
                      <span className="crm-count">{list.length}</span>
                      {addLink && group.addText && (
                        <a href={addLink} target="_blank" rel="noopener noreferrer">
                          {group.addText}
                        </a>
                      )}
                      {keys.length > 0 && (
                        <button
                          type="button"
                          className="crm-text-btn"
                          onClick={() => toggleMany(keys, !groupAll)}
                        >
                          {groupAll ? "Clear" : "Select all"}
                        </button>
                      )}
                    </div>
                    {list.length === 0 ? (
                      <p className="crm-empty">None on this retreat.</p>
                    ) : (
                      <div className="crm-people">
                      {list.map((person) => {
                        const emails = personEmails(person);
                        const key = personKey(person);
                        const selected = selectedKeys.has(key);
                        const disabled = emails.length === 0;
                        return (
                          <label
                            className={`crm-person ${selected ? "is-on" : ""} ${disabled ? "is-disabled" : ""}`}
                            key={key}
                          >
                            <input
                              type="checkbox"
                              checked={selected}
                              disabled={disabled}
                              onChange={() => toggleKey(key)}
                            />
                            <span className="crm-person-copy">
                              <strong>{personName(person)}</strong>
                              <span>
                                {emails.length
                                  ? emails.join(" · ")
                                  : "No email on file"}
                              </span>
                            </span>
                          </label>
                        );
                      })}
                      </div>
                    )}
                  </section>
                );
              })}

              <section className="crm-card crm-card-wide">
                <div className="crm-section-head">
                  <h3>Additional emails</h3>
                  <span className="crm-count">{additionalEmails.length}</span>
                </div>
                <div className="crm-add-row">
                  <input
                    type="email"
                    value={customEmail}
                    onChange={(e) => setCustomEmail(e.target.value)}
                    placeholder="name@email.com"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddCustomEmail();
                      }
                    }}
                  />
                  <button type="button" onClick={handleAddCustomEmail}>
                    Add
                  </button>
                </div>
                <div className="crm-chip-row">
                  {additionalEmails.map((email) => (
                    <span className="crm-chip" key={email}>
                      {email}
                      <button
                        type="button"
                        aria-label={`Remove ${email}`}
                        onClick={() =>
                          setAdditionalEmails((prev) =>
                            prev.filter((item) => item !== email),
                          )
                        }
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </section>
            </div>
          )}
        </div>

        <label className="crm-field">
          <span>Subject</span>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Email subject"
          />
        </label>

        <div className="crm-block-title crm-message-label">Message</div>
        <div className="crm-editor">
        <SunEditor
          onChange={(content) => setMessage(content)}
          setContents={message}
          setOptions={{
            height: "auto",
            minHeight: "460px",
            buttonList: [
              ["undo", "redo"],
              ["bold", "underline", "italic", "removeFormat"],
              ["fontColor", "hiliteColor"],
              ["align", "list", "link"],
              ["fullScreen", "codeView"],
            ],
          }}
        />
        </div>

        <label className="crm-switch">
          <input
            type="checkbox"
            checked={autoRemind && !retreatClosed}
            disabled={retreatClosed || loading}
            onChange={(e) => setAutoRemind(e.target.checked)}
          />
          <span>
            <strong>Send again every 7 days until this retreat is closed</strong>
            <p>
              {retreatClosed
                ? "This retreat is already closed, so automatic reminders stay off."
                : "The email goes out now. The same people receive it again every 7 days until the retreat is validated and closed."}
            </p>
          </span>
        </label>

        {fetchError && <div className="crm-error">{fetchError}</div>}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleModalClose}>
          {cancel}
        </Button>
        <Button variant="primary" onClick={handleSend} disabled={loading}>
          {okay}
          {selectedCount > 0 ? ` (${selectedCount})` : ""}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default MailRecipientsModal;
